import { Injectable } from '@angular/core';
import createAuth0Client from '@auth0/auth0-spa-js';
import Auth0Client from '@auth0/auth0-spa-js/dist/typings/Auth0Client';
import {
  from,
  of,
  Observable,
  BehaviorSubject,
  combineLatest,
  throwError,
} from 'rxjs';
import { tap, catchError, concatMap, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LoadingService } from './loading.service';
import { ApiHttpService } from './api.http.service';
import { APIEndPoints } from '../utils/api-endpoint';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Create an observable of Auth0 instance of client
  auth0Client$ = (
    from(
      createAuth0Client({
        ...environment.auth,
        redirect_uri: `${window.location.origin}`,
      }),
    ) as Observable<Auth0Client>
  ).pipe(
    shareReplay(1), // Every subscription receives the same shared value
    catchError((err) => throwError(err)),
  );
  // Define observables for SDK methods that return promises by default
  // For each Auth0 SDK method, first ensure the client instance is ready
  // concatMap: Using the client instance, call SDK method; SDK returns a promise
  // from: Convert that resulting promise into an observable
  isAuthenticated$ = this.auth0Client$.pipe(
    concatMap((client: Auth0Client) => from(client.isAuthenticated())),
    tap((res) => (this.loggedIn = res)),
    catchError((err) => {
      return throwError(err);
    }),
  );
  handleRedirectCallback$ = this.auth0Client$.pipe(
    concatMap((client: Auth0Client) => from(client.handleRedirectCallback())),
  );
  // Create subject and public observable of user profile data
  public userProfileSubject$ = new BehaviorSubject<any>(null);
  userProfile$ = this.userProfileSubject$.asObservable();
  // Create a local property for login status
  loggedIn: boolean = null;

  constructor(
    private readonly loaderService: LoadingService,
    private readonly _apiService: ApiHttpService,
  ) {
    // On initial load, check authentication state with authorization server
    // Set up local auth streams if user is already authenticated
    this.localAuthSetup();
    // Handle redirect from Auth0 login
    this.handleAuthCallback();
  }

  getTokenSilently$(options?): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) =>
        from(client.getTokenSilently(options)),
      ),
    );
  }

  // When calling, options can be passed if desired
  getUser$(options?): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getUser(options))),
      tap((user) => this.userProfileSubject$.next(user)),
    );
  }

  private localAuthSetup() {
    // This should only be called on app initialization
    // Set up local authentication streams
    const checkAuth$ = this.isAuthenticated$.pipe(
      concatMap((loggedIn: boolean) => {
        if (loggedIn) {
          // If authenticated, get user and set in app
          // NOTE: you could pass options here if needed
          return this.getUser$();
        } else {
          const params = window.location.search;
          if (params.includes('code=') && params.includes('state=')) {
          } else this.userProfileSubject$.next({});
        }
        // If not authenticated, return stream that emits 'false'
        return of(loggedIn);
      }),
    );
    checkAuth$.subscribe();
  }

  login(redirectModule = 'user', app = false, redirectPath: string = '/') {
    this.loaderService.ShowLoading();
    // A desired redirect path can be passed to login method
    // (e.g., from a route guard)
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log in
      client.loginWithRedirect({
        redirect_uri: `${window.location.origin}/${redirectModule}/dashboard`,
        appState: { target: redirectPath },
        apiURL: environment.serviceURL,
        app: app,
      });
    });
  }

  signUp(
    email: string,
    name: string,
    password: string,
    redirectModule: string = 'user',
    redirectPath: string = '/',
  ) {
    this.loaderService.ShowLoading();
    // A desired redirect path can be passed to login method
    // (e.g., from a route guard)
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log in
      client.loginWithRedirect({
        redirect_uri: `${window.location.origin}/${redirectModule}/dashboard`,
        appState: { target: redirectPath },
        screen_hint: 'signup',
        login_hint: email,
        user_fname: name,
        enc_hint: password,
        apiURL: environment.serviceURL,
      });
    });
  }

  private handleAuthCallback() {
    // Call when app reloads after user logs in with Auth0
    const params = window.location.search;
    if (params.includes('code=') && params.includes('state=')) {
      this.loaderService.ShowLoading();
      let targetRoute: string; // Path to redirect to after login processsed
      const authComplete$ = this.handleRedirectCallback$.pipe(
        // delay(500),
        // Have client, now call method to handle auth callback redirect
        tap((cbRes) => {
          // Get and set target redirect route from callback results
          targetRoute =
            cbRes.appState && cbRes.appState.target
              ? cbRes.appState.target
              : '/';
        }),
        concatMap(() => {
          // Redirect callback complete; get user and login status
          return combineLatest([this.getUser$(), this.isAuthenticated$]);
        }),
      );
      // Subscribe to authentication completion observable
      // Response will be an array of user and login status
      authComplete$.subscribe(([user, loggedIn]) => {
        this.loaderService.HideLoading();
        // Redirect to target route after callback processing
        // this.router.navigate([targetRoute]);
      });
    }
  }

  logout(returnPath = '') {
    this.loaderService.ShowLoading();
    this.loggedIn = false;
    this.userProfileSubject$.next({});
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log out
      client.logout({
        client_id: environment.auth.client_id,
        returnTo: `${window.location.origin}${returnPath}`,
      });
    });
  }
  //newsletters
  NewsLetterUserSignUp(data) {
    return this._apiService.post(
      APIEndPoints.NewsLetter + '/NewsLetterUserSignUp',
      data,
    );
  }

  NewsLetterConfirmation(id, data) {
    return this._apiService
      .put(
        `${APIEndPoints.NewsLetter}/NewsLetterUserEmailConfirmation/${id}`,
        data,
      )
      .pipe();
  }
  GetPciSubscriptionToken(data) {
    return this._apiService.post(
      `${APIEndPoints.PciSubscription}/GetPciSubscriptionToken`,
      data,
    );
  }
  //Unsubscribe call
  UnSubscribe(data) {
    return this._apiService.post(
      APIEndPoints.User + '/SaveUnSubscriptionReasons',
      data,
    );
  }
}
